import {
  CartItem,
  CartItemDesign,
  CartItemDesignInstance,
  CartItemDesignInstanceLogo,
  CartItemLogo,
} from "../presentation/screens/order-create/models/CartItem";
import { BaseLogo } from "../core/models/entities/BaseLogo";
import { Design, DesignLogo } from "../core/models/entities/Design";
import { Organization } from "../core/models/entities/Organization";
import { parseJSONWithFallback } from "./functions/JsonParse";

// Cart storage v2 (flag REACT_APP_CART_STORAGE_V2_ENABLED, design
// DESIGN-CART-STORAGE-2026-09-24 rev. 2). CartContext persists its items
// through serializeCartForStorage / deserializeCartFromStorage ONLY while the
// flag is on; off, it keeps today's slimItemsForStorage + parseJSONWithFallback
// untouched.
//
// Format v2 is the SAME key and the SAME root type (CartItem[]) as v1, with:
//   A1 - a design line's instances[].logos[].baseLogo is dropped when, and
//        only when, the design's LIVE designLogos list carries that logo
//        (findLiveDesignLogo) - it is rehydrated from there on read. A logo
//        with no live source (deleted server-side, designLogo without .logo,
//        logoId mismatch) keeps its full copy. excludedDeletedDesignLogos is
//        never a source: useCartSync keeps the instance's own pre-deletion
//        snapshot for those, and so does this module.
//   A2 - every Organization copy in the bucket is reduced to the six fields
//        a cart reader uses; the design loses measurementLines/viewImages
//        (design-editor-only fields).
// No envelope and no version field: a v2 bucket is a valid v1 bucket (every
// removed field is optional or unread), so a build with the flag off - or an
// older build - reads it as-is, and the read is idempotent for v1 buckets
// (an existing baseLogo always wins over the design's copy).

export const CART_STORAGE_QUOTA_TOAST_ID = "cart-storage-quota";
export const CART_STORAGE_QUOTA_I18N_KEY = "orderCreate.cartStorageQuotaExceeded";
// Cart quota toast v2 (flag cartQuotaToastV2Enabled): the softer, truthful
// text - the cart still works in the tab; place the order now.
export const CART_STORAGE_QUOTA_I18N_KEY_V2 =
  "orderCreate.cartStorageQuotaExceededV2";

// The single predicate both directions use: the LIVE designLogos list only,
// matched on dl.logoId (the instance's logoId is set from x.logo.id, and
// every cart reader matches instance rows on dl.logoId), and only a row that
// actually carries its logo counts as a source.
export function findLiveDesignLogo(
  item: Pick<CartItemDesign, "design">,
  logoId: number,
): DesignLogo | undefined {
  return item.design?.designLogos?.find(
    (dl) => dl?.logoId === logoId && !!dl.logo,
  );
}

// The Organization fields a cart reader touches (læser 2's mapping): id /
// parentId / name / type (act-as, vendor mix guard, DesignDetails), navNumber
// (order-details title), mainBranchId (heating / shipping context). The ~34
// other fields (addresses, cvr, contacts, branding, children, parent,
// commission) are never read from a cart copy. Undefined / null stays as it
// is - mapDesign leaves design.customer undefined when the DTO has none, and
// a fabricated {} would satisfy `?.` guards with wrong data.
const ORGANIZATION_FIELDS: ReadonlyArray<keyof Organization> = [
  "id",
  "parentId",
  "name",
  "type",
  "navNumber",
  "mainBranchId",
];

export function slimOrganizationForStorage<T extends Organization | undefined | null>(
  org: T,
): T {
  if (org == null || typeof org !== "object") return org;
  const slim: Partial<Organization> = {};
  ORGANIZATION_FIELDS.forEach((field) => {
    const value = (org as Organization)[field];
    if (value !== undefined) (slim as Record<string, unknown>)[field] = value;
  });
  return slim as T;
}

// BaseLogo itself keeps EVERY field (transferMethod, displayImage, product,
// approval, colorDetails ... are read unguarded by several cart components);
// only its organization copies are whitelisted: `customer`, and the API's
// `vendor` org that rides along on the mapper's DTO spread (not on the TS
// type, never read from a cart copy - mapLogo already folded it into
// `customer` for vendor-owned logos).
type BaseLogoWithVendor = BaseLogo & { vendor?: Organization };

function slimBaseLogoForStorage(logo: BaseLogo): BaseLogo {
  if (!logo || typeof logo !== "object") return logo;
  const source = logo as BaseLogoWithVendor;
  const slim: BaseLogoWithVendor = { ...source };
  if ("customer" in source) {
    slim.customer = slimOrganizationForStorage(source.customer);
  }
  if ("vendor" in source) {
    slim.vendor = slimOrganizationForStorage(source.vendor);
  }
  return slim;
}

function slimDesignLogoForStorage(dl: DesignLogo): DesignLogo {
  if (!dl || typeof dl !== "object" || !dl.logo) return dl;
  return { ...dl, logo: slimBaseLogoForStorage(dl.logo) };
}

function slimDesignForStorage(design: Design): Design {
  if (!design || typeof design !== "object") return design;
  // Destructure the two editor-only collections away; every other field is
  // kept (style / styleViews / designLogos[].view are read by the print,
  // placement and popup components).
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const { measurementLines, viewImages, ...rest } = design;
  const slim: Design = { ...rest } as Design;
  if ("customer" in design) {
    slim.customer = slimOrganizationForStorage(design.customer);
  }
  if ("vendor" in design) {
    slim.vendor = slimOrganizationForStorage(design.vendor);
  }
  if (Array.isArray(design.designLogos)) {
    slim.designLogos = design.designLogos.map(slimDesignLogoForStorage);
  }
  return slim;
}

function slimInstanceLogoForStorage(
  item: CartItemDesign,
  logo: CartItemDesignInstanceLogo,
): CartItemDesignInstanceLogo {
  if (!logo || typeof logo !== "object") return logo;
  if (logo.baseLogo === undefined) return logo;
  if (findLiveDesignLogo(item, logo.logoId)) {
    // A1: the design's own copy is the source on read - drop this one.
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    const { baseLogo, ...reference } = logo;
    return reference as CartItemDesignInstanceLogo;
  }
  // No live source: keep the full copy (only its org copy is whitelisted).
  return { ...logo, baseLogo: slimBaseLogoForStorage(logo.baseLogo) };
}

function slimDesignItemForStorage(item: CartItemDesign): CartItemDesign {
  const slim: CartItemDesign = { ...item };
  if ("design" in item) slim.design = slimDesignForStorage(item.design);
  if (Array.isArray(item.instances)) {
    slim.instances = item.instances.map((inst: CartItemDesignInstance) =>
      inst && typeof inst === "object" && Array.isArray(inst.logos)
        ? {
            ...inst,
            logos: inst.logos.map((l) => slimInstanceLogoForStorage(item, l)),
          }
        : inst,
    );
  }
  if (Array.isArray(item.excludedDeletedDesignLogos)) {
    slim.excludedDeletedDesignLogos = item.excludedDeletedDesignLogos.map(
      slimDesignLogoForStorage,
    );
  }
  return slim;
}

function slimLogoItemForStorage(item: CartItemLogo): CartItemLogo {
  // A logo line's baseLogo is read unguarded by the order payload mapper
  // (baseLogo.product.sku, baseLogo.logoType) - it is ALWAYS kept in full.
  if (!("baseLogo" in item)) return item;
  return { ...item, baseLogo: slimBaseLogoForStorage(item.baseLogo) };
}

// One structural map over the items, then ONE JSON.stringify of the small
// result - no stringify -> parse(reviver) -> stringify round trip over a
// multi-MB string. Date objects become ISO strings exactly as today.
export function serializeCartForStorage(items: readonly CartItem[]): string {
  const slimmed = (items ?? []).map((item) => {
    if (!item || typeof item !== "object") return item;
    if (item.type === "Design") {
      return slimDesignItemForStorage(item as CartItemDesign);
    }
    if (item.type === "Logo") {
      return slimLogoItemForStorage(item as CartItemLogo);
    }
    return item;
  });
  return JSON.stringify(slimmed);
}

function rehydrateDesignItem(item: CartItemDesign): CartItemDesign {
  if (!Array.isArray(item.instances)) return item;
  let changed = false;
  const instances = item.instances.map((inst) => {
    if (!inst || typeof inst !== "object" || !Array.isArray(inst.logos)) {
      return inst;
    }
    let instChanged = false;
    const logos = inst.logos.map((l) => {
      // An existing copy always wins: a v1 bucket reads exactly as today,
      // and any copy left in a v2 bucket is one without a live source (the
      // write predicate guarantees it) - kept, as useCartSync keeps it.
      if (!l || typeof l !== "object" || l.baseLogo !== undefined) return l;
      const live = findLiveDesignLogo(item, l.logoId);
      if (!live) return l;
      instChanged = true;
      // The very same reference as the design's copy - the shape a line has
      // right after it was added to the cart.
      return { ...l, baseLogo: live.logo };
    });
    if (!instChanged) return inst;
    changed = true;
    return { ...inst, logos };
  });
  return changed ? { ...item, instances } : item;
}

// Parses a bucket (v1 or v2) into CartItem[]. Same parse as today
// (parseJSONWithFallback), then A1 rehydration on design lines. A bucket
// that is not an array (corrupt) yields an empty cart instead of an object
// that crashes items.map downstream.
export function deserializeCartFromStorage(
  raw: string | null | undefined,
): CartItem[] {
  if (!raw) return [];
  const parsed: unknown = parseJSONWithFallback(raw, []);
  if (!Array.isArray(parsed)) return [];
  return (parsed as CartItem[]).map((item) =>
    item && typeof item === "object" && item.type === "Design"
      ? rehydrateDesignItem(item as CartItemDesign)
      : item,
  );
}

// The two quota names browsers use (Firefox's legacy NS_ code included) -
// the same test the flag-off path makes inline.
export function isStorageQuotaError(error: unknown): boolean {
  const name = (error as { name?: unknown } | null)?.name;
  return name === "QuotaExceededError" || name === "NS_ERROR_DOM_QUOTA_REACHED";
}

export interface CartStorageQuotaDiagnostics {
  key: string;
  chars: number;
  lines: number;
  instances: number;
  parkedBuckets: number;
  parkedChars: number;
  // The four origin fields below are measured only with `scanOrigin` (flag
  // cartQuotaDiagEnabled); otherwise they stay 0 / [] - not measured.
  // Sum of the length of every value in the Storage (the origin's total),
  // except the fallback's ~35-char `_products_shadow` markers, which are
  // never read here (the flag-off path must not touch a marker; the
  // fallback module owns them).
  originChars: number;
  // The live bucket's stored value plus this user's parked buckets.
  ownChars: number;
  // originChars minus ownChars: what OTHER code on this origin (other
  // scripts, extensions, older keys) holds of the shared quota.
  foreignChars: number;
  // The five largest keys by value length, names sanitised: this user's
  // b2CId becomes `me`, every other GUID becomes `other`.
  topKeys: Array<[string, number]>;
}

export type CartStorageQuotaClass =
  | "cart-quota"
  | "cart-quota-empty"
  | "cart-quota-foreign"
  | "cart-quota-parked";

// Below this many chars on the origin AFTER the failed write (foreign +
// parked + the payload) a quota error means the browser granted (almost)
// no quota at all: blocked storage, a private window, a locked-down
// profile. Every mainstream browser allows at least ~2.5 M UTF-16 chars
// per origin.
export const CART_QUOTA_EMPTY_ORIGIN_CHARS = 1_000_000;

const GUID_RE = /[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}/gi;
const SHADOW_MARKER_KEY_SUFFIX = "_products_shadow";
const TOP_KEYS = 5;

function escapeRegExp(s: string): string {
  return s.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
}

// What the console gets when a write hits the quota: the payload that
// failed, its shape, how much of the quota this user's OTHER parked
// identity buckets hold (they share the origin's quota with the live one),
// and - because a 1-4 line cart of 22-142 K chars cannot fill a 5 M quota
// on its own (the two 30/9 reports) - what the WHOLE origin holds: the
// total (originChars), this user's own share (ownChars), the foreign
// remainder (foreignChars: other scripts, extensions, older keys) and the
// five largest keys (topKeys) with identities sanitised (own b2CId -> me,
// any other GUID -> other) so the line can be pasted into a ticket. The
// fallback's `_products_shadow` markers are skipped, never read: the
// flag-off path must not touch a marker (T12 pin). One pass over the
// Storage, getItem per key, no writes. Reads the given Storage only -
// never throws (a throwing Storage yields zeros and an empty topKeys).
// classifyCartStorageQuota turns the result into the Clarity error code.
//
// `scanOrigin` (flag cartQuotaDiagEnabled) switches the whole-origin pass
// on. Off (the default) is the original pass: getItem on this user's OTHER
// parked buckets only - never on the live key or any foreign key - and the
// four origin fields stay 0 / [].
export function describeCartStorageQuota(
  storage: Pick<Storage, "length" | "key" | "getItem">,
  b2CId: string | undefined,
  key: string,
  payloadChars: number,
  items: readonly CartItem[],
  scanOrigin: boolean = false,
): CartStorageQuotaDiagnostics {
  let instances = 0;
  (items ?? []).forEach((item) => {
    if (item?.type === "Design") {
      instances += (item as CartItemDesign).instances?.length ?? 0;
    }
  });
  let parkedBuckets = 0;
  let parkedChars = 0;
  let originChars = 0;
  let ownChars = 0;
  let topKeys: Array<[string, number]> = [];
  try {
    const own = new RegExp(`^${escapeRegExp(b2CId ?? "")}_\\d+_products$`);
    const sizes: Array<[string, number]> = [];
    for (let i = 0; i < storage.length; i++) {
      const k = storage.key(i);
      if (!k) continue;
      if (!scanOrigin) {
        if (k === key || !own.test(k)) continue;
        parkedBuckets += 1;
        parkedChars += storage.getItem(k)?.length ?? 0;
        continue;
      }
      if (k.endsWith(SHADOW_MARKER_KEY_SUFFIX)) continue;
      const len = storage.getItem(k)?.length ?? 0;
      originChars += len;
      sizes.push([k, len]);
      if (k === key) {
        ownChars += len;
      } else if (own.test(k)) {
        parkedBuckets += 1;
        parkedChars += len;
        ownChars += len;
      }
    }
    if (scanOrigin) {
      const meRe = b2CId ? new RegExp(escapeRegExp(b2CId), "g") : undefined;
      topKeys = sizes
        .sort((a, b) => b[1] - a[1])
        .slice(0, TOP_KEYS)
        .map(([k, len]) => [
          (meRe ? k.replace(meRe, "me") : k).replace(GUID_RE, "other"),
          len,
        ]);
    }
  } catch {
    // Diagnostics only - a throwing Storage must not mask the quota error.
  }
  return {
    key,
    chars: payloadChars,
    lines: items?.length ?? 0,
    instances,
    parkedBuckets,
    parkedChars,
    originChars,
    ownChars,
    foreignChars: Math.max(0, originChars - ownChars),
    topKeys,
  };
}

// The Clarity error code for a quota episode (trackErrorShown), from a
// diagnostic taken with scanOrigin. What the origin would hold had the
// write succeeded is foreignChars + parkedChars + chars (the payload
// replaces the live bucket's OLD value, so that value does not count).
// Below CART_QUOTA_EMPTY_ORIGIN_CHARS that total cannot fill any real
// quota -> "cart-quota-empty": the browser blocks or barely grants
// storage (where a 1-4 line cart lands unless something foreign is found).
// Otherwise the largest of the three shares names the filler:
// "cart-quota-foreign" (other keys on the origin), "cart-quota-parked"
// (this user's parked act-as buckets) or "cart-quota" (the payload itself);
// a tie goes to the earlier one. Pure; never throws.
export function classifyCartStorageQuota(
  d: Pick<CartStorageQuotaDiagnostics, "chars" | "foreignChars" | "parkedChars">,
): CartStorageQuotaClass {
  const total = d.foreignChars + d.parkedChars + d.chars;
  if (total < CART_QUOTA_EMPTY_ORIGIN_CHARS) return "cart-quota-empty";
  if (d.foreignChars >= d.parkedChars && d.foreignChars >= d.chars) {
    return "cart-quota-foreign";
  }
  if (d.parkedChars >= d.chars) return "cart-quota-parked";
  return "cart-quota";
}
